const Events = require('../events/index');
const Core = require('../core');
const { getUserObject } = require('../services/userService');
const { getDestinationObject } = require('../services/destinationService');
const { getSearchTermsObject } = require('../services/searchTermsService');
const { getDealObject } = require('../services/dealService');
const { getPointsObject } = require('../services/pointsService');
const { getBookingObject } = require('../services/bookingService');
const { getCouponObject } = require('../services/couponService');

const { DealUtils } = require('../../entities');

const _popupExposureCount = {};

function _getAndIncrementPopupCount(search_id) {
    const key = search_id || '__unknown__';
    _popupExposureCount[key] = (_popupExposureCount[key] || 0) + 1;
    return _popupExposureCount[key];
}

// Flattened for Mixpanel, which charts poorly over nested objects. Every key is present on every
// rate-check event so the two suggest-alternative cohorts stay directly comparable. Prices come
// from the deals already on the page and are unverified - alts_prices_verified says so.
function _buildAlternativesSnapshotMetadata(alternatives_snapshot) {
    const alternative = alternatives_snapshot?.would_be_alternative ?? null;
    const match = alternatives_snapshot?.best_similar_in_list ?? null;
    const clicked = alternatives_snapshot?.clicked ?? null;

    return {
        alts_clicked_room: clicked?.room ?? null,
        alts_clicked_price: clicked?.price ?? null,
        alts_clicked_board: clicked?.board ?? null,
        alts_clicked_deadline: clicked?.cancellation_deadline ?? null,
        alts_group_pick_room: alternative?.room ?? null,
        alts_group_pick_price: alternative?.price ?? null,
        alts_group_pick_board: alternative?.board ?? null,
        alts_group_pick_deadline: alternative?.cancellation_deadline ?? null,
        alts_group_pick_rejection_reason: alternatives_snapshot?.alternative_rejection_reason ?? null,
        alts_match_room: match?.room ?? null,
        alts_match_price: match?.price ?? null,
        alts_match_board: match?.board ?? null,
        alts_match_deadline: match?.cancellation_deadline ?? null,
        alts_match_group_id: match?.group_id ?? null,
        alts_match_supplier: match?.supplier ?? null,
        alts_match_similarity: alternatives_snapshot?.match_similarity ?? null,
        alts_match_matched_by: alternatives_snapshot?.match_matched_by ?? null,
        alts_match_blocked_reason: alternatives_snapshot?.match_blocked_reason ?? null,
        alts_near_miss_similarity: alternatives_snapshot?.near_miss_similarity ?? null,
        alts_near_miss_room: alternatives_snapshot?.near_miss_room ?? null,
        alts_candidates_considered: alternatives_snapshot?.candidates_considered ?? null,
        alts_match_candidates_considered: alternatives_snapshot?.match_candidates_considered ?? null,
        alts_regret_delta: alternatives_snapshot?.regret_delta ?? null,
        alts_clicked_vs_match_delta: alternatives_snapshot?.clicked_vs_match_delta ?? null,
        alts_prices_verified: alternatives_snapshot ? !!alternatives_snapshot.prices_verified : null,
    };
}

function _buildCompanyMetadata(user) {
    const company = user?.company;
    return {
        company_id: company?.id ?? company?._id ?? null,
        company_name: company?.name ?? null,
    };
}

function _buildRateCheckMetadata({
    popup_type,
    recheck_type,
    unavailable_reason,
    rejection_reason,
    price_changed,
    old_price,
    new_price,
    deadline_changed,
    board_changed,
    old_board,
    new_board,
    old_cancellation_deadline,
    new_cancellation_deadline,
    deal,
    search_id,
    hotel,
    suggest_alternative_flag,
    alternatives_snapshot,
}) {
    const price_change_direction = price_changed ? (new_price > old_price ? 'increase' : 'decrease') : null;
    const changes_presented = [];
    if (price_changed) {
        changes_presented.push(price_change_direction === 'increase' ? 'price_increase' : 'price_decrease');
    }
    if (deadline_changed) {
        changes_presented.push('policy_change');
    }
    if (board_changed) {
        changes_presented.push('board_change');
    }

    return {
        popup_type,
        recheck_type: recheck_type ?? null,
        unavailable_reason: unavailable_reason ?? null,
        rejection_reason: rejection_reason ?? null,
        changes_presented,
        price_change_direction,
        original_price: old_price ?? null,
        new_price: new_price ?? null,
        price_delta:
            price_changed && typeof old_price === 'number' && typeof new_price === 'number'
                ? Math.abs(new_price - old_price)
                : null,
        currency: deal?.currency ?? null,
        policy_changed: !!deadline_changed,
        board_changed: !!board_changed,
        old_board: old_board ?? null,
        new_board: new_board ?? null,
        old_cancellation_deadline: old_cancellation_deadline ?? null,
        new_cancellation_deadline: new_cancellation_deadline ?? null,
        search_id: search_id ?? null,
        hotel_id: hotel?.id ?? hotel?._id ?? deal?.hotel?.id ?? deal?.hotel?._id ?? deal?.details?.hotel_id ?? null,
        hotel_name: hotel?.name ?? hotel?.title ?? deal?.hotel?.name ?? deal?.hotel?.title ?? deal?.details?.hotel_name ?? null,
        room_type: deal?.room ?? null,
        suggest_alternative_flag: suggest_alternative_flag ?? null,
        ..._buildAlternativesSnapshotMetadata(alternatives_snapshot),
        supplier_id: deal ? (DealUtils.dealSupplierExtractor(deal).supplier ?? null) : null,
    };
}

function fireResults(count, wait_time_ml) {
    const metadata = {
        count,
        wait_time_ml
    };

    Core.trackEvent(Events.SEARCH.RESULTS_RECEIVED_FIRE, metadata);
}

function fireFilteredResults(count, wait_time_ml) {
    const metadata = {
        count,
        wait_time_ml
    };

    Core.trackEvent(Events.SEARCH.RESULTS_RECEIVED_HOTEL_FILTERS, metadata);
}

function hotelsResults(count, wait_time_ml, context = {}) {
    const { user, destination, search, hotels } = context;
    const metadata = {
        count,
        wait_time_ml,
        ...getUserObject(user),
        ...getDestinationObject(destination),
        ...getSearchTermsObject(search),
        ...(Array.isArray(hotels) && hotels.length > 0 && { hotels }),
    };

    Core.trackEvent(Events.SEARCH.RESULTS_RECEIVED_HOTELS, metadata);
}

function resultsFetchingTime(load_time) {
    const metadata = {
        load_time,
    }
    
    Core.trackEvent(Events.RESULTS.RESULTS_FETCHING_TIME, metadata);
}

function searchWithoutResults(user, destination, search) {
    const metadata = {
        ...getUserObject(user),
        ...getDestinationObject(destination),
        ...getSearchTermsObject(search),
    };

    Core.trackEvent(Events.RESULTS.SEARCH_WITHOUT_RESULTS, metadata);
}

function searchedHotelWithoutDeals(user, destination, search) {
    const metadata = {
        ...getUserObject(user),
        ...getDestinationObject(destination),
        ...getSearchTermsObject(search),
    };

    Core.trackEvent(Events.RESULTS.SEARCHED_HOTEL_WITHOUT_DEALS, metadata);
}

function searchWithoutResultsHotel(user, destination, search) {
    const metadata = {
        ...getUserObject(user),
        ...getDestinationObject(destination),
        ...getSearchTermsObject(search),
    };

    Core.trackEvent(Events.RESULTS.SEARCH_WITHOUT_RESULTS_HOTEL, metadata);
}

function rateUnavailable(room_name, price, hotel_name) {
    const metadata = {
        room_name,
        price,
        hotel_name,
    };

    Core.trackEvent(Events.RESULTS.RATE_UNAVAILABLE, metadata);
}

function bookingCreated({
                            user,
                            destination,
                            hotel,
                            search,
                            deal,
                            payment_method,
                            charge_currency,
                            points,
                            geolocationSearch,
                            distance,
                            attempt_count,
                            session_attempt_count,
                            hotel_attempt_count,
                            booking_id,
                            status,
                            triggered_by_rate_check,
                            rate_check_change_types,
                            points_toggle_state_at_booking,
                            coupon,
                            bnpl,
                        }) {
    const points_redeemed = (points && points.show && points.user_chosen_points) || 0;
    const metadata = {
        ...getUserObject(user),
        ...getDestinationObject(destination, hotel),
        ...getSearchTermsObject(search),
        ...getDealObject(deal),
        ...getPointsObject(points),
        ...getCouponObject(coupon, charge_currency || (deal && deal.currency)),
        payment_method,
        charge_currency,
        geolocationSearch,
        distance,
        attempt_count,
        session_attempt_count,
        hotel_attempt_count,
        booking_id,
        status,
        triggered_by_rate_check: triggered_by_rate_check ?? false,
        rate_check_change_types: rate_check_change_types ?? null,
        points_redeemed,
        ...(points_toggle_state_at_booking && { points_toggle_state_at_booking }),
        ...(bnpl || {}),
    };

    Core.trackEvent(Events.RESERVATION.BOOKING_CREATED, metadata);
}

function reservationCanceled(user, reservation_id, destination, booking) {
    const metadata = {
        ...getUserObject(user),
        reservation_id,
        ...getDestinationObject(destination),
        ...getBookingObject(booking),
    };

    Core.trackEvent(Events.RESERVATION.RESERVATION_CANCELED, metadata);
}

function reservationCanceledFailed(user, reservation_id, hotel_name) {
    const metadata = {
        ...getUserObject(user),
        reservation_id,
        hotel_name
    };

    Core.trackEvent(Events.RESERVATION.RESERVATION_CANCELED_FAILED, metadata);
}

function reservationFailed({
                               user,
                               destination,
                               deal,
                               payment_method,
                               error,
                               hotel,
                               status,
                               failure_reason,
                               attempt_count,
                               session_attempt_count,
                               hotel_attempt_count,
                           }) {
    const metadata = {
        ...getUserObject(user),
        ...getDestinationObject(destination, hotel),
        ...getDealObject(deal),
        payment_method,
        error,
        status,
        failure_reason,
        attempt_count,
        session_attempt_count,
        hotel_attempt_count,
    };
    Core.trackEvent(Events.RESERVATION.RESERVATION_FAILED_TO_BOOK, metadata);
}

function httpError(request, respond, endpoint) {
    const metadata = {
        request,
        respond,
        endpoint
    };

    Core.trackEvent(Events.HTTP.HTTP_ERROR, metadata);
}

function roomRateCheckPopupShown(props) {
    const popup_exposure_count_in_search = _getAndIncrementPopupCount(props.search_id);
    const metadata = {
        ..._buildRateCheckMetadata(props),
        ..._buildCompanyMetadata(props.user),
        popup_exposure_count_in_search,
    };
    Core.trackEvent(Events.RESERVATION.ROOM_RATE_CHECK_POPUP_SHOWN, metadata);
    return popup_exposure_count_in_search;
}

function roomRateCheckPopupAction(props) {
    const metadata = {
        action: props.action,
        ..._buildRateCheckMetadata(props),
        popup_exposure_count_in_search: props.popup_exposure_count_in_search ?? null,
    };
    Core.trackEvent(Events.RESERVATION.ROOM_RATE_CHECK_POPUP_ACTION, metadata);
}

module.exports = {
    fireResults,
    fireFilteredResults,
    hotelsResults,
    resultsFetchingTime,
    searchWithoutResults,
    searchWithoutResultsHotel,
    rateUnavailable,
    bookingCreated,
    reservationCanceled,
    reservationCanceledFailed,
    reservationFailed,
    httpError,
    searchedHotelWithoutDeals,
    roomRateCheckPopupShown,
    roomRateCheckPopupAction,
};
